import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, Outlet, useParams } from "react-router";
import { AdminAlert } from "@/components/shell/AdminPage";
import { useHermes } from "@/components/hermes/HermesProvider";
import { useAccountWorkspace } from "@/lib/account-workspace";
import {
  BrandSiteApiError,
  fetchBrandSites,
  type BrandSite,
} from "@/lib/brand-site-api";
import { brandSiteListPath } from "@/lib/brand-site-routing";
import { shouldReplaceBrandSiteAuthoring } from "@/lib/hermes-brand-site-authoring";

type BrandSiteWorkbenchValue = {
  sites: BrandSite[];
  currentSite: BrandSite;
  reload: () => Promise<void>;
};

const BrandSiteWorkbenchContext = createContext<BrandSiteWorkbenchValue | null>(null);

export function useBrandSiteWorkbench(): BrandSiteWorkbenchValue {
  const value = useContext(BrandSiteWorkbenchContext);
  if (!value) {
    throw new Error("useBrandSiteWorkbench 须在 BrandSiteSiteLayout 内使用");
  }
  return value;
}

/** 已选站壳：校验 siteId、同步 Hermes 不串站；切站在顶栏面包屑 */
export default function BrandSiteSiteLayout() {
  const { brandId, siteId } = useParams();
  const { accountId } = useAccountWorkspace();
  const {
    prepareBrandSiteAuthoring,
    startBrandSiteAuthoringSession,
    brandSiteAuthoring,
  } = useHermes();

  const [sites, setSites] = useState<BrandSite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!brandId) return;
    setError(null);
    const payload = await fetchBrandSites(accountId, brandId);
    setSites(payload.brand_sites ?? []);
  }, [accountId, brandId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void load()
      .catch((caught) => {
        if (cancelled) return;
        const message =
          caught instanceof BrandSiteApiError
            ? caught.message
            : caught instanceof TypeError
              ? import.meta.env.DEV
                ? "暂时连不上服务。本地请先启动品牌站（:3020）。"
                : "暂时连不上服务，请稍后重试"
              : "暂时没法打开，请稍后重试";
        setError(message);
        setSites([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const currentSite = useMemo(
    () => sites.find((site) => String(site.id) === String(siteId)) ?? null,
    [sites, siteId],
  );

  useEffect(() => {
    if (!brandId || !currentSite) return;
    const ctx = {
      mode: "brand_site_authoring" as const,
      accountId,
      brandId: Number(brandId),
      brandSiteId: currentSite.id,
    };
    prepareBrandSiteAuthoring(ctx);
    if (shouldReplaceBrandSiteAuthoring(brandSiteAuthoring, currentSite.id)) {
      void startBrandSiteAuthoringSession(ctx);
    }
  }, [
    accountId,
    brandId,
    brandSiteAuthoring,
    currentSite,
    prepareBrandSiteAuthoring,
    startBrandSiteAuthoringSession,
  ]);

  if (loading) {
    return <p className="text-sm text-muted-foreground">加载中…</p>;
  }

  if (error) {
    return <AdminAlert>{error}</AdminAlert>;
  }

  if (!brandId || !siteId) {
    return <p className="text-sm text-muted-foreground">缺少站点。</p>;
  }

  if (!currentSite) {
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-foreground">找不到这座官网</p>
        <p className="text-sm text-muted-foreground">可能已删除，或不属于这个品牌。</p>
        <Link
          to={brandSiteListPath(accountId, brandId)}
          className="text-sm text-primary-readable hover:underline"
        >
          回到官网列表
        </Link>
      </div>
    );
  }

  const value: BrandSiteWorkbenchValue = {
    sites,
    currentSite,
    reload: load,
  };

  return (
    <BrandSiteWorkbenchContext.Provider value={value}>
      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
    </BrandSiteWorkbenchContext.Provider>
  );
}
